import {
  AfterViewInit,
  AfterContentInit,
  ChangeDetectionStrategy,
  Component,
  ContentChild,
  ContentChildren,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  QueryList,
  SimpleChanges,
  TemplateRef,
  ViewChild
} from '@angular/core';
import { MatColumnDef, MatTable, MatTableDataSource } from '@angular/material/table';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { CdkTableDataSourceInput } from '@angular/cdk/table';
import { Subscription } from 'rxjs';
import { SelectionModel } from '@angular/cdk/collections';

export type DataTableRowClass<T> =
  | string
  | string[]
  | Set<string>
  | { [klass: string]: any }
  | ((row: T, index: number) => string | string[] | Set<string> | { [klass: string]: any });

export type DataTablePreset = 'admin' | 'modal' | 'compact';

export interface DataTableSelectionConfig<T> {
  name?: string;
  selection: SelectionModel<T>;
  canSelect?: (row: T) => boolean;
  hideHeaderCheckbox?: boolean;
}

export interface DataTableAction<T> {
  key: string;
  label?: string;
  icon?: string;
  color?: 'primary' | 'accent' | 'warn';
  variant?: 'icon' | 'stroked' | 'flat';
  disabled?: (row: T) => boolean;
  visible?: (row: T) => boolean;
  className?: string | ((row: T) => string);
}

export interface DataTableActionColumn<T> {
  name?: string;
  align?: 'start' | 'center' | 'end';
  actions: DataTableAction<T>[];
}

export interface DataTableActionEvent<T> {
  action: DataTableAction<T>;
  row: T;
  index: number;
  nativeEvent: Event;
}

@Component({
  selector: 'app-data-table',
  templateUrl: './data-table.component.html',
  styleUrls: ['./data-table.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DataTableComponent<T> implements AfterContentInit, AfterViewInit, OnChanges, OnDestroy {
  @Input() dataSource: CdkTableDataSourceInput<T> = [];
  @Input() displayedColumns: string[] = [];
  @Input() preset: DataTablePreset = 'admin';
  @Input() tableClass = '';
  @Input() shellClass = '';
  @Input() emptyMessage = 'No hay datos disponibles.';
  @Input() emptyIcon = 'inbox';
  @Input() loading = false;
  @Input() loadingMessage = 'Cargando datos...';
  @Input() rowClickable = false;
  @Input() rowClass: DataTableRowClass<T> | null = null;
  @Input() stickyHeader = false;
  @Input() showPaginator = false;
  @Input() selectionColumn?: DataTableSelectionConfig<T> | null = null;
  @Input() actionColumn?: DataTableActionColumn<T> | null = null;
  @Input() length = 0;
  @Input() pageSize = 10;
  @Input() pageIndex = 0;
  @Input() pageSizeOptions: number[] = [10, 25, 50];
  @Input() showFirstLastButtons = true;
  @Input() clientPagination = false;

  @Output() rowClick = new EventEmitter<{ row: T; index: number }>();
  @Output() selectionToggleAll = new EventEmitter<void>();
  @Output() selectionToggleRow = new EventEmitter<T>();
  @Output() actionClick = new EventEmitter<DataTableActionEvent<T>>();
  @Output() page = new EventEmitter<PageEvent>();

  @ContentChildren(MatColumnDef) projectedColumnDefs!: QueryList<MatColumnDef>;
  @ContentChild('emptyState', { read: TemplateRef }) emptyStateTemplate?: TemplateRef<unknown>;
  @ContentChild('loadingState', { read: TemplateRef }) loadingStateTemplate?: TemplateRef<unknown>;
  @ViewChild(MatTable, { static: true }) table!: MatTable<T>;
  @ViewChild(MatPaginator) paginator?: MatPaginator;

  private columnsChangeSub?: Subscription;
  private registeredColumns = new Map<string, MatColumnDef>();

  get colspan(): number {
    return this.displayedColumns?.length || 1;
  }

  get shellClasses(): string[] {
    return ['data-table-shell', `data-table-shell--${this.preset}`, this.shellClass].filter(Boolean);
  }

  get selectionColumnName(): string {
    return this.selectionColumn?.name || 'select';
  }

  get actionColumnName(): string {
    return this.actionColumn?.name || 'actions';
  }

  ngAfterContentInit(): void {
    this.syncProjectedColumns();
    this.columnsChangeSub = this.projectedColumnDefs.changes.subscribe(() => {
      this.syncProjectedColumns();
      this.table.renderRows();
    });
  }

  ngAfterViewInit(): void {
    this.attachPaginatorIfSupported();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['dataSource'] || changes['showPaginator'] || changes['clientPagination']) {
      this.attachPaginatorIfSupported();
    }
  }

  ngOnDestroy(): void {
    this.columnsChangeSub?.unsubscribe();
  }

  handleRowClick(row: T, index: number): void {
    if (!this.rowClickable) {
      return;
    }

    this.rowClick.emit({ row, index });
  }

  emitPageChange(event: PageEvent): void {
    this.page.emit(event);
  }

  resolveRowClass(row: T, index: number): string | string[] | Set<string> | { [klass: string]: any } | null {
    if (!this.rowClass) {
      return null;
    }

    return typeof this.rowClass === 'function' ? this.rowClass(row, index) : this.rowClass;
  }

  isAllSelected(): boolean {
    if (!this.selectionColumn?.selection) {
      return false;
    }

    const selectableRows = this.getSelectableRows();
    return selectableRows.length > 0 && selectableRows.every((row) => this.selectionColumn?.selection.isSelected(row));
  }

  isSelectionIndeterminate(): boolean {
    if (!this.selectionColumn?.selection) {
      return false;
    }

    const selectableRows = this.getSelectableRows();
    const selectedCount = selectableRows.filter((row) => this.selectionColumn?.selection.isSelected(row)).length;
    return selectedCount > 0 && selectedCount < selectableRows.length;
  }

  isRowSelectable(row: T): boolean {
    return this.selectionColumn?.canSelect ? this.selectionColumn.canSelect(row) : true;
  }

  isRowSelected(row: T): boolean {
    return this.selectionColumn?.selection?.isSelected(row) ?? false;
  }

  onToggleAll(): void {
    this.selectionToggleAll.emit();
  }

  onToggleRow(row: T, event?: Event): void {
   // event?.stopPropagation();
    this.selectionToggleRow.emit(row);
  }

  onActionClick(action: DataTableAction<T>, row: T, index: number, nativeEvent: Event): void {
    nativeEvent.stopPropagation();
    this.actionClick.emit({ action, row, index, nativeEvent });
  }

  isActionVisible(action: DataTableAction<T>, row: T): boolean {
    return action.visible ? action.visible(row) : true;
  }

  isActionDisabled(action: DataTableAction<T>, row: T): boolean {
    return action.disabled ? action.disabled(row) : false;
  }

  getActionClass(action: DataTableAction<T>, row: T): string {
    return typeof action.className === 'function' ? action.className(row) : action.className || '';
  }

  private syncProjectedColumns(): void {
    const nextColumns = new Map<string, MatColumnDef>();

    this.projectedColumnDefs.forEach((columnDef) => {
      nextColumns.set(columnDef.name, columnDef);

      if (!this.registeredColumns.has(columnDef.name)) {
        this.table.addColumnDef(columnDef);
      }
    });

    this.registeredColumns.forEach((columnDef, name) => {
      if (!nextColumns.has(name)) {
        this.table.removeColumnDef(columnDef);
      }
    });

    this.registeredColumns = nextColumns;
  }

  private attachPaginatorIfSupported(): void {
    if (!(this.dataSource instanceof MatTableDataSource)) {
      return;
    }

    this.dataSource.paginator = this.showPaginator && this.clientPagination ? this.paginator ?? null : null;
  }

  private getSelectableRows(): T[] {
    const rows = this.extractRows();
    return rows.filter((row) => this.isRowSelectable(row));
  }

  private extractRows(): T[] {
    if (this.dataSource instanceof MatTableDataSource) {
      return this.dataSource.data;
    }

    return Array.isArray(this.dataSource) ? this.dataSource : [];
  }

  preventCheckboxProp(event: Event): void {
    if (event) {
      event.stopPropagation();
    }
  }

}
